import React from 'react';
import {
  Box,
  VStack,
  HStack,
  Heading,
  Text,
  Button,
  Icon,
  Divider,
  Grid,
  Stat,
  StatLabel,
  StatNumber,
  Alert,
  AlertIcon,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
  Link,
  useColorModeValue,
  useDisclosure,
} from '@chakra-ui/react';
import {
  FiPhone,
  FiMail,
  FiMessageSquare,
  FiExternalLink,
  FiBriefcase,
  FiPlus,
} from 'react-icons/fi';
import moment from 'moment';
import { useHistory } from 'react-router-dom';
import { PROPERTY_TYPES, LOAN_PURPOSES, OCCUPANCY_TYPES } from '../../../constants/crmConstants';
import StageBadge from '../../../components/CRM/StageBadge';
import DialpadCallButton from '../../../components/CRM/Dialpad/DialpadCallButton';
import DialpadSMSButton from '../../../components/CRM/Dialpad/DialpadSMSButton';
import EmailTemplateModal from '../../../components/CRM/Actions/EmailTemplateModal';
import SMSTemplateModal from '../../../components/CRM/Actions/SMSTemplateModal';
import DealPartiesManager from '../../../components/CRM/DealPartiesManager';
import AriveSyncPanel from '../../../components/CRM/Arive/AriveSyncPanel';

/**
 * DealRail — the right column of the Person Workspace.
 *
 * Everything about the SELECTED deal: quick outreach actions (identical
 * DNC gating to DealPage), the Arive sync panel embedded as-is, read-only
 * deal vitals + property/loan details, and the Related Parties manager.
 * Deep edits stay on the classic deal page — one click away.
 */

// Post-application stages deep-link to Arive's loan center; leads link to the
// lead view. Ported verbatim from DealPage.
const LOAN_CENTER_STAGES = [
  'loan-application-intake', 'loan-app-intake',
  'loan-qualification', 'loan-preapproved',
  'loan-setup', 'loan-disclosure-sent', 'loan-submitted',
  'loan-approved-conditions', 'loan-resubmittal',
  'loan-clear-to-close', 'loan-docs-out', 'loan-docs-signed',
  'loan-funded', 'loan-brokercheck-received', 'loan-finalized',
  'loan-processing', 'deal-won', // legacy
];

const ariveUrlForDeal = (deal) => {
  if (!deal?.ariveId) return null;
  return deal.stage && LOAN_CENTER_STAGES.includes(deal.stage)
    ? `https://future.myarive.com/app/loans/${deal.ariveId}/loan-center`
    : `https://future.myarive.com/app/leads/${deal.ariveId}/`;
};

const labelFor = (options, value) => options.find((o) => o.value === value)?.label || value || '—';

export default function DealRail({ contact, deal, dialpadEnabled, onCreateDeal }) {
  const history = useHistory();

  const { isOpen: isEmailOpen, onOpen: onEmailOpen, onClose: onEmailClose } = useDisclosure();
  const { isOpen: isSMSOpen, onOpen: onSMSOpen, onClose: onSMSClose } = useDisclosure();

  const cardBG = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const mutedColor = useColorModeValue('gray.600', 'gray.400');

  const propertyAddress = deal?.propertyAddress || {};
  const hasProperty = propertyAddress.street || propertyAddress.city;
  const ariveUrl = ariveUrlForDeal(deal);

  // Named targets resolve the contact-vs-deal ambiguity ("Call Carol")
  const firstName = contact.firstName || 'Contact';

  return (
    <VStack align="stretch" spacing={4}>
      {/* Quick actions — one compact row, same DNC gating as DealPage */}
      <Box bg={cardBG} borderRadius="xl" borderWidth="1px" borderColor={borderColor} shadow="sm" p={3}>
        <VStack align="stretch" spacing={2}>
          <HStack spacing={2} align="stretch">
            {dialpadEnabled ? (
              <>
                <Box flex={1} minW={0}>
                  <DialpadCallButton
                    phoneNumber={contact.phone}
                    contactName={`${contact.firstName} ${contact.lastName}`}
                    dealId={deal?.id}
                    contactId={contact.id}
                    isDisabled={contact.dnc || contact.dncCall}
                    size="sm"
                    label={`Call ${firstName}`}
                  />
                </Box>
                <Box flex={1} minW={0}>
                  <DialpadSMSButton
                    phoneNumber={contact.phone}
                    contactName={`${contact.firstName} ${contact.lastName}`}
                    dealId={deal?.id}
                    contactId={contact.id}
                    deal={deal}
                    contact={contact}
                    isDisabled={contact.dnc || contact.dncSms}
                    size="sm"
                    buttonLabel={`Text ${firstName}`}
                  />
                </Box>
              </>
            ) : (
              <>
                <Button
                  leftIcon={<FiPhone />}
                  size="sm"
                  colorScheme="blue"
                  flex={1}
                  minW={0}
                  isDisabled={!contact.phone || contact.dnc || contact.dncCall}
                  as={Link}
                  href={`tel:${contact.phone}`}
                >
                  Call {firstName}
                </Button>
                <Button
                  leftIcon={<FiMessageSquare />}
                  size="sm"
                  colorScheme="green"
                  flex={1}
                  minW={0}
                  isDisabled={contact.dnc || contact.dncSms}
                  onClick={onSMSOpen}
                >
                  Text {firstName}
                </Button>
              </>
            )}
            <Button
              leftIcon={<FiMail />}
              size="sm"
              colorScheme="purple"
              flex={1}
              minW={0}
              isDisabled={contact.dnc || contact.dncEmail}
              onClick={onEmailOpen}
            >
              Email {firstName}
            </Button>
          </HStack>

          {(contact.dnc || contact.dncCall || contact.dncEmail || contact.dncSms) && (
            <Alert status="error" size="sm" borderRadius="md">
              <AlertIcon />
              <Text fontSize="xs">
                {contact.dnc
                  ? 'DNC: all outreach disabled'
                  : `DNC: ${[contact.dncCall && 'calling', contact.dncSms && 'texting', contact.dncEmail && 'emailing'].filter(Boolean).join(', ')} disabled`}
              </Text>
            </Alert>
          )}
        </VStack>
      </Box>

      {deal ? (
        <>
          {/* Arive LOS panel — embedded as-is (owns its own sync flows) */}
          <AriveSyncPanel deal={deal} onDealUpdate={() => {}} />

          {/* Deal vitals (read-only; edit on the classic page) */}
          <Box bg={cardBG} borderRadius="xl" borderWidth="1px" borderColor={borderColor} shadow="sm">
            <Box p={4}>
              <HStack justify="space-between" mb={3} align="flex-start">
                <VStack align="flex-start" spacing={1}>
                  <Heading size="sm">Deal Vitals</Heading>
                  <StageBadge stage={deal.stage} />
                </VStack>
                <VStack align="flex-end" spacing={1}>
                  <Button
                    size="xs"
                    variant="outline"
                    rightIcon={<FiExternalLink />}
                    onClick={() => history.push(`/crm/deals/${deal.id}`)}
                  >
                    Classic deal page
                  </Button>
                  {ariveUrl && (
                    <Button
                      size="xs"
                      colorScheme="teal"
                      variant="outline"
                      rightIcon={<FiExternalLink />}
                      onClick={() => window.open(ariveUrl, '_blank', 'noopener,noreferrer')}
                    >
                      Open Arive
                    </Button>
                  )}
                </VStack>
              </HStack>
              <Grid templateColumns="repeat(2, minmax(0, 1fr))" gap={3}>
                <Stat size="sm" minW={0}>
                  <StatLabel fontSize="xs">Loan Amount</StatLabel>
                  <StatNumber fontSize="lg" color="green.600" noOfLines={1}>
                    {deal.amount > 0 ? `$${deal.amount.toLocaleString()}` : '—'}
                  </StatNumber>
                </Stat>
                <Stat size="sm" minW={0}>
                  <StatLabel fontSize="xs">Est. Closing</StatLabel>
                  <StatNumber fontSize="lg" noOfLines={1}>
                    {deal.estimatedClosingDate ? moment(deal.estimatedClosingDate).format('MM/DD/YY') : '—'}
                  </StatNumber>
                </Stat>
                <Stat size="sm" minW={0}>
                  <StatLabel fontSize="xs">Product</StatLabel>
                  <StatNumber fontSize="md" textTransform="capitalize" noOfLines={1}>{deal.product || '—'}</StatNumber>
                </Stat>
                <Stat size="sm" minW={0}>
                  <StatLabel fontSize="xs">Source</StatLabel>
                  <StatNumber fontSize="md" textTransform="capitalize" noOfLines={1}>{deal.source || '—'}</StatNumber>
                </Stat>
                <Stat size="sm" minW={0}>
                  <StatLabel fontSize="xs">Loan #</StatLabel>
                  <StatNumber fontSize="md" noOfLines={1}>{deal.loanNumber || '—'}</StatNumber>
                </Stat>
                <Stat size="sm" minW={0}>
                  <StatLabel fontSize="xs">Lender</StatLabel>
                  <StatNumber fontSize="md" noOfLines={1}>{deal.lender || '—'}</StatNumber>
                </Stat>
                <Stat size="sm" minW={0}>
                  <StatLabel fontSize="xs">Next Action</StatLabel>
                  <StatNumber fontSize="md" textTransform="capitalize" noOfLines={1}>
                    {deal.nextAction ? deal.nextAction.replace(/-/g, ' ') : '—'}
                  </StatNumber>
                </Stat>
                <Stat size="sm" minW={0}>
                  <StatLabel fontSize="xs">Next Due</StatLabel>
                  <StatNumber fontSize="md" noOfLines={1}>
                    {deal.nextActionDue ? moment(deal.nextActionDue).format('MM/DD/YY') : '—'}
                  </StatNumber>
                </Stat>
              </Grid>
            </Box>
            <Divider />

            {/* Property & Loan + Related Parties */}
            <Accordion allowMultiple defaultIndex={[1]}>
              <AccordionItem borderTopWidth={0}>
                <h2>
                  <AccordionButton>
                    <Box flex="1" textAlign="left" fontWeight="semibold" fontSize="sm">Property & Loan</Box>
                    <AccordionIcon />
                  </AccordionButton>
                </h2>
                <AccordionPanel pb={4}>
                  <VStack align="stretch" spacing={2} fontSize="sm">
                    <HStack justify="space-between">
                      <Text color={mutedColor}>Purpose</Text>
                      <Text>{labelFor(LOAN_PURPOSES, deal.loanPurpose)}</Text>
                    </HStack>
                    <HStack justify="space-between">
                      <Text color={mutedColor}>Property Type</Text>
                      <Text>{labelFor(PROPERTY_TYPES, deal.propertyType)}</Text>
                    </HStack>
                    <HStack justify="space-between">
                      <Text color={mutedColor}>Occupancy</Text>
                      <Text>{labelFor(OCCUPANCY_TYPES, deal.occupancyType || 'primary_residence')}</Text>
                    </HStack>
                    <HStack justify="space-between" align="flex-start">
                      <Text color={mutedColor}>Address</Text>
                      <Text textAlign="right">
                        {hasProperty
                          ? [propertyAddress.street, propertyAddress.city, propertyAddress.state, propertyAddress.zip]
                              .filter(Boolean)
                              .join(', ')
                          : '—'}
                      </Text>
                    </HStack>
                    <Text fontSize="xs" color={mutedColor} pt={1}>
                      Read-only here — use the classic deal page for deep edits.
                    </Text>
                  </VStack>
                </AccordionPanel>
              </AccordionItem>

              <AccordionItem borderBottomWidth={0}>
                <h2>
                  <AccordionButton>
                    <Box flex="1" textAlign="left" fontWeight="semibold" fontSize="sm">
                      Related Parties
                      {deal.relatedParties?.length > 0 && (
                        <Text as="span" ml={2} fontSize="xs" color={mutedColor}>
                          ({deal.relatedParties.length})
                        </Text>
                      )}
                    </Box>
                    <AccordionIcon />
                  </AccordionButton>
                </h2>
                <AccordionPanel pb={4}>
                  <DealPartiesManager deal={deal} onPartyAdded={() => {}} onPartyRemoved={() => {}} />
                </AccordionPanel>
              </AccordionItem>
            </Accordion>
          </Box>
        </>
      ) : (
        /* No deal selected */
        <Box
          bg={cardBG}
          borderRadius="xl"
          borderWidth="1px"
          borderStyle="dashed"
          borderColor={borderColor}
          p={6}
          textAlign="center"
        >
          <VStack spacing={3}>
            <Icon as={FiBriefcase} boxSize={8} color="gray.300" />
            <Text fontSize="sm" color={mutedColor}>
              No deal for this person yet. Outreach still works — and when they're ready,
              start a deal without leaving this page.
            </Text>
            <Button size="sm" colorScheme="green" leftIcon={<FiPlus />} onClick={onCreateDeal}>
              Create Deal
            </Button>
          </VStack>
        </Box>
      )}

      {/* Outreach modals (deal may be null — same as the contact drawer) */}
      <EmailTemplateModal isOpen={isEmailOpen} onClose={onEmailClose} deal={deal || null} contact={contact} />
      <SMSTemplateModal isOpen={isSMSOpen} onClose={onSMSClose} deal={deal || null} contact={contact} />
    </VStack>
  );
}
